import { Config } from '@adornis/config/config.js';
import { logger } from '@adornis/base/logging.js';
import { randomUUID } from 'node:crypto';
import type {
  SNAvailabilityEngineer,
  SNClientListResponse,
  SNEngineerAvailabilityResponse,
  SNEngineerListResponse,
  SNPudoLocation,
  SNPudoLocationListResponse,
  SNOAuthTokenResponse,
  SNSmartTeamClient,
  SNSmartTeamEngineer,
  SNSmartTeamTicket,
  SNTicketDetailResponse,
  SNTicketEvent,
  SNTicketEventAction,
  SNTicketListParams,
  SNTicketNote,
  SNTicketListResponse,
} from './types.js';

const smartTeamConfig = new Config('servicenow-smartteam', {
  SERVICENOW_INSTANCE_URL: { name: 'SERVICENOW_INSTANCE_URL', env: true, client: false, required: true },
  SERVICENOW_CLIENT_ID: { name: 'SERVICENOW_CLIENT_ID', env: true, client: false, required: true },
  SERVICENOW_CLIENT_SECRET_B64: { name: 'SERVICENOW_CLIENT_SECRET_B64', env: true, client: false, required: true },
});

let cachedToken: { token: string; expiresAt: number } | null = null;
let pendingTokenRequest: Promise<string> | null = null;

async function getAccessToken(): Promise<string> {
  if (cachedToken && Date.now() < cachedToken.expiresAt) {
    return cachedToken.token;
  }

  if (pendingTokenRequest) return pendingTokenRequest;

  pendingTokenRequest = (async () => {
    try {
      const instanceUrl = smartTeamConfig.get('SERVICENOW_INSTANCE_URL');
      const clientId = smartTeamConfig.get('SERVICENOW_CLIENT_ID');
      const clientSecret = Buffer.from(smartTeamConfig.get('SERVICENOW_CLIENT_SECRET_B64'), 'base64').toString('utf-8');

      const { res, text } = await loggedFetch(
        `${instanceUrl}/oauth_token.do`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'client_credentials',
            client_id: clientId,
            client_secret: clientSecret,
          }),
        },
        { op: 'getAccessToken' },
      );

      let data: SNOAuthTokenResponse;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`ServiceNow auth failed (${res.status}): ${text.slice(0, 200)}`);
      }

      if (!res.ok || (data as any).error_description) {
        throw new Error(`ServiceNow auth failed: ${(data as any).error_description || text.slice(0, 200)}`);
      }

      cachedToken = {
        token: data.access_token,
        expiresAt: Date.now() + (data.expires_in - 60) * 1000,
      };

      return cachedToken.token;
    } finally {
      pendingTokenRequest = null;
    }
  })();

  return pendingTokenRequest;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

const apiBase = () => `${smartTeamConfig.get('SERVICENOW_INSTANCE_URL')}/api/x_nscgg_csb_smrttm/smart_team`;

/** Cap log bodies so a noisy response can't blow up the log line. */
const LOG_BODY_MAX = 2000;

const truncate = (s: string): string => (s.length > LOG_BODY_MAX ? `${s.slice(0, LOG_BODY_MAX)}…(${s.length} chars)` : s);

/**
 * Redact secrets from anything that ends up in a log line. Strips Bearer tokens,
 * `client_secret` values, and `access_token`s from the OAuth response. The integration
 * partner asked for request/response visibility by default; this keeps the logs safe
 * to ship without leaking creds.
 */
function redactForLog(value: string): string {
  return value
    .replace(/(client_secret=)[^&\s"]+/gi, '$1[redacted]')
    .replace(/("access_token"\s*:\s*")[^"]+(")/gi, '$1[redacted]$2')
    .replace(/(Bearer\s+)[A-Za-z0-9._-]+/g, '$1[redacted]');
}

/**
 * Centralised HTTP wrapper for every SmartTeam API call. Always logs the outbound
 * request (method + URL + redacted body) at info, plus the response status + body
 * snippet — info on 2xx, error on anything else. Returns the parsed `Response` plus
 * the raw text body (so callers don't double-read).
 *
 * Intentionally always-on (not behind a flag): the partner relies on this signal
 * to diagnose normalisation issues like the 202-vs-200 case.
 */
async function loggedFetch(
  url: string,
  init: RequestInit,
  context: Record<string, unknown> = {},
): Promise<{ res: Response; text: string }> {
  const method = (init.method ?? 'GET').toUpperCase();
  const bodyForLog =
    typeof init.body === 'string'
      ? redactForLog(init.body)
      : init.body instanceof URLSearchParams
        ? redactForLog(init.body.toString())
        : undefined;

  logger.info(
    { ...context, method, url, body: bodyForLog ? truncate(bodyForLog) : undefined },
    'ServiceNow request',
  );

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (err) {
    logger.error({ ...context, method, url, err }, 'ServiceNow request failed (network)');
    throw err;
  }

  const text = await res.text();
  const logBody = { ...context, method, url, statusCode: res.status, body: truncate(redactForLog(text)) };

  if (res.ok) {
    logger.info(logBody, 'ServiceNow response');
  } else {
    logger.error(logBody, 'ServiceNow response (non-2xx)');
  }

  return { res, text };
}

/**
 * Fetch tickets from the SmartTeams custom API. All parameters are optional;
 * `clientId` filters to a single customer (per spec §4.6 / §4.11 update).
 */
export async function getTicketList(params: SNTicketListParams = {}): Promise<SNSmartTeamTicket[]> {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.sortBy) qs.set('sort_by', params.sortBy);
  if (params.sortDirection) qs.set('sort_direction', params.sortDirection);
  if (params.query) qs.set('query', params.query);
  if (params.clientId) qs.set('client_id', params.clientId);

  const url = `${apiBase()}/ticket_list${qs.size > 0 ? `?${qs.toString()}` : ''}`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getTicketList' });

  if (!res.ok) throw new Error('Failed to fetch ticket list from ServiceNow');

  const data = JSON.parse(text) as SNTicketListResponse;
  // The live API wraps each row as `{ ticket_details, part_requirements }`; older spec revisions
  // returned the ticket flat. Unwrap defensively so both shapes map correctly (otherwise every
  // field reads as undefined → tickets render with no title). Fold the row's `part_requirements`
  // onto the ticket so the overview can show part numbers without a per-ticket details fetch.
  return (data.result.ticket_list ?? []).map(row => {
    const ticket = ((row as any)?.ticket_details ?? row) as SNSmartTeamTicket;
    if (Array.isArray((row as any)?.part_requirements)) {
      ticket.parts = (row as any).part_requirements;
    }
    return ticket;
  });
}

/**
 * Fetch the list of clients (customers) known to the SmartTeams custom API.
 * Used to map a SmartTeamsAppUser (Customer) onto a ServiceNow client sys_id.
 */
export async function getClientList(): Promise<SNSmartTeamClient[]> {
  const url = `${apiBase()}/client_list`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getClientList' });

  if (!res.ok) throw new Error('Failed to fetch client list from ServiceNow');

  const data = JSON.parse(text) as SNClientListResponse;
  return data.result.client_list;
}

/**
 * Fetch the list of field engineers (agents) from the SmartTeams custom API.
 * Drives the searchable dropdown in the SmartTeams admin backend so admins
 * never have to type a sys_id by hand.
 */
export async function getEngineerList(): Promise<SNSmartTeamEngineer[]> {
  const url = `${apiBase()}/engineer_list`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getEngineerList' });

  if (!res.ok) throw new Error('Failed to fetch engineer list from ServiceNow');

  const data = JSON.parse(text) as SNEngineerListResponse;
  return data.result.agent_list;
}

/**
 * Fetch every PUDO depot (parts pickup/drop-off point) known to the SmartTeams custom API.
 *
 * The rows are flat strings rather than the `{ value, display_value }` wrapper the ticket endpoints
 * use, and carry no coordinates — the caller geocodes `street`/`zip_code`/`city` to place them on a
 * map. 100 depots on nsctest (2026-09-17), of which roughly two thirds list opening hours.
 */
export async function getPudoLocationList(): Promise<SNPudoLocation[]> {
  const url = `${apiBase()}/pudo_location_list`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getPudoLocationList' });

  if (!res.ok) throw new Error('Failed to fetch PUDO location list from ServiceNow');

  const data = JSON.parse(text) as SNPudoLocationListResponse;
  return data.result.pudo_locations ?? [];
}

/**
 * Fetch one engineer's schedule from the SmartTeams custom API.
 *
 * This is the only endpoint that exposes an engineer's home location: `engineer_list` carries just
 * name and email, and the bulk `availability/find` covers a subset of the roster (51 of 139 on
 * nsctest, 2026-09-17) while this one answers for every engineer. Note the live payload leaves
 * `engineer_location.country`, `.street`, `.zip` and `.state` empty and puts the postcode in
 * `.name` — in practice only `city` and the coordinates are populated.
 *
 * `from`/`to` bound the schedule window and are required by the API even when the caller only
 * wants the location; they default to a one-day window starting now.
 */
export async function getEngineerAvailability(
  engineerId: string,
  from: Date = new Date(),
  to: Date = new Date(Date.now() + 24 * 60 * 60 * 1000),
): Promise<SNAvailabilityEngineer> {
  const qs = new URLSearchParams({
    engineer_id: engineerId,
    from_date: formatGmtTimestamp(from),
    to_date: formatGmtTimestamp(to),
  });

  const url = `${apiBase()}/availability/engineer?${qs.toString()}`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getEngineerAvailability', engineerId });

  if (!res.ok) throw new Error(`Failed to fetch availability for engineer ${engineerId} (HTTP ${res.status})`);

  const data = JSON.parse(text) as SNEngineerAvailabilityResponse;
  return data.result;
}

/**
 * Fetch a single ticket's details by ticket number.
 */
export async function getTicketDetails(ticketNumber: string): Promise<SNSmartTeamTicket> {
  const url = `${apiBase()}/ticket_details?number=${encodeURIComponent(ticketNumber)}`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getTicketDetails', ticketNumber });

  if (!res.ok) throw new Error(`Failed to fetch ticket details for ${ticketNumber}`);

  const data = JSON.parse(text) as Record<string, any>;

  if (data.result?.error) {
    throw new Error(`Ticket not found: ${ticketNumber}`);
  }

  const ticket = (data as SNTicketDetailResponse).result.ticket_details;

  if (!ticket.assigned_to?.value && ticket.assignment?.assigned_to?.value) {
    ticket.assigned_to = ticket.assignment.assigned_to;
  }

  // Part requirements now arrive as a sibling of `ticket_details` (not nested under it). Fold them
  // onto the ticket so callers keep reading `ticket.parts`. Falls back to any nested `parts` for
  // forward/backward compatibility if the shape changes again.
  ticket.parts = (data as SNTicketDetailResponse).result.part_requirements ?? ticket.parts ?? [];

  return ticket;
}

/**
 * Fetch a ticket's note streams. Separate from {@link getTicketDetails} because the notes are
 * siblings of `ticket_details` that the ticket type has no room for, and `ticket_list` never
 * returns them — the colleague list has to fetch them per ticket.
 */
export async function getTicketNotes(ticketNumber: string): Promise<{
  workNotes: SNTicketNote[];
  comments: SNTicketNote[];
  handlingNotes: SNTicketNote[];
}> {
  const url = `${apiBase()}/ticket_details?number=${encodeURIComponent(ticketNumber)}`;
  const { res, text } = await loggedFetch(url, { headers: await authHeaders() }, { op: 'getTicketNotes', ticketNumber });

  if (!res.ok) throw new Error(`Failed to fetch notes for ${ticketNumber}`);

  const result = (JSON.parse(text) as SNTicketDetailResponse).result;
  return {
    workNotes: result?.work_notes ?? [],
    comments: result?.comments ?? [],
    handlingNotes: result?.handling_notes ?? [],
  };
}

/**
 * Format a Date as `yyyy-mm-dd hh:mm:ss` in GMT, per spec §1.4 data constraints.
 */
function formatGmtTimestamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(
    date.getUTCHours(),
  )}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

/**
 * Post a `ticket.fe.updated` (or other) event to the SmartTeams ticket_event endpoint.
 * Spec section 4 (Ticket Updates).
 */
export async function postTicketEvent(params: {
  ticketId: string;
  action: SNTicketEventAction;
  payload?: Record<string, unknown>;
  /** Override the event name. Defaults to `ticket.fe.updated`. */
  event?: string;
  /**
   * When the event actually happened, per the FE. Defaults to "now" — overridden
   * for backdated workflow actions (FE forgot to tap Travel Start when they left).
   */
  eventTime?: Date;
}): Promise<{ status: number }> {
  const body: SNTicketEvent = {
    event: params.event ?? 'ticket.fe.updated',
    event_id: randomUUID(),
    event_time: formatGmtTimestamp(params.eventTime ?? new Date()),
    source: 'smart_team',
    correlationId: params.ticketId,
    ticket_id: params.ticketId,
    data: {
      action: params.action,
      ...(params.payload ? { payload: params.payload } : {}),
    },
  };

  const url = `${apiBase()}/ticket_event`;
  const { res, text } = await loggedFetch(
    url,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(body),
    },
    { op: 'postTicketEvent', ticketId: params.ticketId, action: params.action, eventId: body.event_id },
  );

  if (!res.ok) {
    throw new Error(
      `ServiceNow ticket_event ${params.action} failed (HTTP ${res.status}) for ${params.ticketId}` +
        (text ? `: ${text.slice(0, 200)}` : ''),
    );
  }

  return { status: res.status };
}
